import React, { useState } from "react";
import PropTypes from "prop-types";
import { useAuth, useAIRoleBuilderTasksContext } from "hooks";
import { prepareAIRoleData } from "utils";
import { AIRoleBuilderTasksList } from "components/Missions/Roles/AIRoleBuilder";
import { RoleForm } from "components/Missions/Roles";

/**
 * AIRoleBuilderTasks
 *
 * The queue bar plus the review flow it drives. Choosing "Review" opens the
 * regular role form prefilled from the generated results; saving it discards the queued
 * request automatically. Cancelling leaves the request in the queue, still reviewable.
 *
 * @param {string}  missionId  Mission the reviewed role is created under
 * @param {string}  domainId   Domain the reviewed role is created in
 */
const AIRoleBuilderTasks = ({ missionId, domainId, ...props }) => {
  const { id: userId } = useAuth();
  const { discardTask } = useAIRoleBuilderTasksContext();
  const [reviewingTask, setReviewingTask] = useState(null);
  const [roleFormData, setRoleFormData] = useState(null);

  const handleReview = (task) => {
    const data = prepareAIRoleData(task.results, { userId });

    if (!data) return;

    setReviewingTask(task);
    setRoleFormData(data);
  };

  const closeRoleForm = () => {
    setReviewingTask(null);
    setRoleFormData(null);
  };

  // Only a successful save resolves the request. Closing or cancelling the form leaves
  // the row in the queue so the generated result is not thrown away until discarded manually.
  const handleRoleCreated = async (result) => {
    const { id } = result?.addMissionRole?.missionRole || {};

    await discardTask(reviewingTask.id, { missionRoleId: id });
    closeRoleForm();
  };

  return (
    <>
      <AIRoleBuilderTasksList onReview={handleReview} {...props} />
      {reviewingTask && roleFormData && (
        <RoleForm
          onClose={closeRoleForm}
          onCompleted={handleRoleCreated}
          data={roleFormData}
          isEditing={false}
          missionId={missionId}
          domainId={domainId}
          show
        />
      )}
    </>
  );
};

AIRoleBuilderTasks.propTypes = {
  missionId: PropTypes.string,
  domainId: PropTypes.string,
};

export default AIRoleBuilderTasks;
